import styles from './more-courses-by-instructors.module.scss';
import {LocalizedHtml, useI18n} from '@udemy-v2/i18n';
import {
    BrowseEventTrackingProvider,
    BackendSourceOptions,
    TrackableDiscoveryItem,
    DiscoveryItemTypeOptions,
} from '@udemy-v2/react-browse-event-tracking';
import {useClpCourse} from '@/features/course-landing-page/context/clp-course-provider.react-component';
import {useMoreCoursesByInstructor} from '@/lib/hooks/use-more-courses-by-instructor';
import {BrowseCourseWithInstructorDetails} from '@/types/course';
import {Instructor} from '@udemy-v2/browse-course';
import {ResponsiveBrowseCourseCarousel} from '@/features/course-landing-page/components/browse-course-carousel/responsive-browse-course-carousel.react-component';

export interface MoreCoursesByInstructorProps {
    instructors: Instructor[];
}

export interface MoreCoursesByInstructorsProps {
    instructor: Instructor;
    moreCoursesByInstructor: TrackableDiscoveryItem<BrowseCourseWithInstructorDetails>[];
}

export const MoreCoursesByInstructors = ({instructors}: MoreCoursesByInstructorProps) => {
    const coursesByInstructor = useMoreCoursesByInstructor();
    return (
        <>
            {instructors.map((instructor) => {
                if (!instructor.url) return null;
                return (
                    <MoreCoursesByInstructor
                        key={`MoreCoursesByInstructors-${instructor.url}`}
                        instructor={instructor}
                        moreCoursesByInstructor={coursesByInstructor[instructor.url] ?? []}
                    />
                );
            })}
        </>
    );
};

const MoreCoursesByInstructor = ({
    instructor,
    moreCoursesByInstructor,
}: MoreCoursesByInstructorsProps) => {
    const {gettext, interpolate} = useI18n();
    const course = useClpCourse();

    if (moreCoursesByInstructor.length === 0) return null;

    const displayedCourses =
        moreCoursesByInstructor.length > 3
            ? moreCoursesByInstructor.slice(0, 3)
            : moreCoursesByInstructor;

    return (
        <div key={`MoreCoursesByInstructor-${instructor.url}`} className={styles['margin-top-xs']}>
            <LocalizedHtml
                html={interpolate(
                    gettext('More Courses by <a class="instructorLink">%(instructorName)s</a>'),
                    {
                        instructorName: instructor.name,
                    },
                    true,
                )}
                interpolate={{
                    instructorLink: (
                        <a className={'ud-heading-xl ud-link-underline'} href={instructor.url} />
                    ),
                }}
                className="ud-heading-xl"
            />
            <BrowseEventTrackingProvider
                browseEventTrackingContext={{
                    relatedSourceId: course.id,
                    relatedSourceType: DiscoveryItemTypeOptions.COURSE,
                    backendSource: BackendSourceOptions.TAUGHT_COURSES,
                }}
            >
                <ResponsiveBrowseCourseCarousel
                    courses={displayedCourses}
                    className={styles['margin-top-xs']}
                />
            </BrowseEventTrackingProvider>
        </div>
    );
};
